import { cache } from "react";
import { getClient } from "./client";

// Rethrow so `next build` fails loudly instead of prerendering a permanent 404
// for a live page. NODE_ENV is "production" at build (every app) and at runtime,
// so a transient runtime error surfaces as a 500 rather than a stale 404 — accepted.
export function failBuildOnError(err: unknown): void {
  if (process.env.NODE_ENV === "production") throw err;
}

// =============================================================================
// TYPES
// =============================================================================

export interface SanityLiveContent {
  isLive?: boolean;
}

// =============================================================================
// SLUG VALIDATION
// =============================================================================

export function validateSlug(slug: string): boolean {
  if (!slug || typeof slug !== "string") return false;
  if (slug.includes("..") || slug.includes("/") || slug.includes("\\"))
    return false;
  if (slug.includes("\0") || slug.includes("\n") || slug.includes("\r"))
    return false;

  const validSlugPattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
  if (!validSlugPattern.test(slug)) return false;

  if (slug.length > 100) return false;

  return true;
}

// =============================================================================
// SECURITY
// =============================================================================

export function isAccessible(
  data: SanityLiveContent | null,
  isInPreviewMode: boolean,
): data is SanityLiveContent {
  if (!data) return false;
  if (data.isLive === true) return true;
  return isInPreviewMode;
}

export function isPreviewParam(
  searchParams: Record<string, string | string[] | undefined>,
): boolean {
  const value = searchParams["preview"];
  const resolved = Array.isArray(value) ? value[0] : value;
  return resolved === "true";
}

// =============================================================================
// FETCHERS (slug-based)
// =============================================================================

export function createStaticFetcher<T>(query: string, projectTag: string) {
  const client = getClient(projectTag, false);
  return cache(async (slug: string, tag: string): Promise<T | null> => {
    try {
      return await client.fetch<T>(query, { slug }, { tag });
    } catch (err) {
      console.error(`[${tag}] Failed to fetch data for slug: ${slug}`, err);
      failBuildOnError(err);
      return null;
    }
  });
}

export function createDynamicFetcher<T>(query: string, projectTag: string) {
  const publishedClient = getClient(projectTag, false);
  const previewClient = getClient(projectTag, true);
  return cache(
    async (
      slug: string,
      usePreviewClient: boolean,
      tag: string,
    ): Promise<[T | null, boolean]> => {
      try {
        const { draftMode } = await import("next/headers");
        const { isEnabled: hasDraftCookie } = await draftMode();

        const isPreviewMode = hasDraftCookie && usePreviewClient;
        const currentClient = isPreviewMode ? previewClient : publishedClient;

        const data = await currentClient.fetch<T>(query, { slug }, { tag });
        return [data, isPreviewMode];
      } catch (err) {
        console.error(`[${tag}] Failed to fetch data for slug: ${slug}`, err);
        return [null, false];
      }
    },
  );
}

// =============================================================================
// FETCHERS (singleton — for pages without a slug param)
// =============================================================================

export function createStaticSingletonFetcher<T>(
  query: string,
  projectTag: string,
) {
  const client = getClient(projectTag, false);
  return cache(async (tag: string): Promise<T | null> => {
    try {
      return await client.fetch<T>(query, {}, { tag });
    } catch (err) {
      console.error(`[${tag}] Failed to fetch singleton data`, err);
      failBuildOnError(err);
      return null;
    }
  });
}

export function createDynamicSingletonFetcher<T>(
  query: string,
  projectTag: string,
) {
  const publishedClient = getClient(projectTag, false);
  const previewClient = getClient(projectTag, true);
  return cache(
    async (
      usePreviewClient: boolean,
      tag: string,
    ): Promise<[T | null, boolean]> => {
      try {
        const { draftMode } = await import("next/headers");
        const { isEnabled: hasDraftCookie } = await draftMode();

        const isPreviewMode = hasDraftCookie && usePreviewClient;
        const currentClient = isPreviewMode ? previewClient : publishedClient;

        const data = await currentClient.fetch<T>(query, {}, { tag });
        return [data, isPreviewMode];
      } catch (err) {
        console.error(`[${tag}] Failed to fetch singleton data`, err);
        return [null, false];
      }
    },
  );
}

// =============================================================================
// STATIC PARAMS
// =============================================================================

interface FetchStaticParamsOptions {
  query: string;
  contentType: string;
  tag: string;
  projectTag: string;
}

export async function fetchStaticParams(
  options: FetchStaticParamsOptions,
): Promise<{ slug: string }[]> {
  const { query, contentType, tag, projectTag } = options;
  const client = getClient(projectTag, false);

  try {
    const items = await client.fetch<{ slug: string }[]>(query, {}, { tag });

    if (!Array.isArray(items)) {
      throw new Error(`Expected array but got: ${typeof items}`);
    }

    console.log(
      `[${contentType}] generateStaticParams: Found ${items.length} pages to statically generate`,
    );

    return items.map((item) => ({ slug: item.slug }));
  } catch (err) {
    console.error(`[${contentType}] Failed to generate static params:`, err);

    if (process.env.NODE_ENV === "production") {
      throw new Error(
        `[${contentType}] Production build failure: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }

    return [];
  }
}
